import React from 'react';
import { useQuery, useQueryClient } from 'react-query';

import { getShopperEmails } from 'apis/ShopRequestAPI';
import { canManageShopperEmails } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';
import { PaginatedResponseType } from 'types/common';
import useDataList from 'utils/hooks/useDataList';
import { useDebounce } from 'utils/hooks/useDebounce';

import { SHOPPER_EMAILS_PAGE_SIZE } from '../constants';
import { ShopperEmailsFiltersType } from './ShopperEmailsContext';
import { EntryModalState } from './ShopperEmailsPage.types';
import { ShopperEmailType } from '../types';

export const useShopperEmailsPage = () => {
  const { currentUser } = useAppSelector((state) => state.auth);
  const queryClient = useQueryClient();

  const [entryModal, setEntryModal] = React.useState<EntryModalState>({ open: false });

  const { items, totalItemsCount, filters, setItems, setPage, setFilters } = useDataList({
    initialState: {
      filters: {
        page: 1,
        pageSize: SHOPPER_EMAILS_PAGE_SIZE,
        search: '',
        mailboxStatus: [],
        linkedShopRequests: [],
        labelPresence: [],
        ownerIds: [],
      },
    },
  });

  const shopperEmailFilters = filters as ShopperEmailsFiltersType;
  const debouncedSearch = useDebounce(shopperEmailFilters.search, 300);

  React.useEffect(() => {
    setItems({ results: [], count: 0 });
    setFilters({ ownerIds: [] });
  }, [currentUser?.user_id, setFilters, setItems]);

  const { data, isLoading, refetch } = useQuery<PaginatedResponseType<ShopperEmailType>>({
    queryKey: [
      'shopper-emails',
      currentUser?.user_id,
      shopperEmailFilters.page,
      shopperEmailFilters.pageSize,
      debouncedSearch,
      shopperEmailFilters.mailboxStatus,
      shopperEmailFilters.linkedShopRequests,
      shopperEmailFilters.labelPresence,
      shopperEmailFilters.ownerIds,
    ],
    queryFn: () =>
      getShopperEmails({
        page: shopperEmailFilters.page,
        search: debouncedSearch.trim() || undefined,
        mailbox_status: shopperEmailFilters.mailboxStatus.length ? shopperEmailFilters.mailboxStatus : undefined,
        linked_shop_requests: shopperEmailFilters.linkedShopRequests.length
          ? shopperEmailFilters.linkedShopRequests
          : undefined,
        label_presence: shopperEmailFilters.labelPresence.length ? shopperEmailFilters.labelPresence : undefined,
        owner_id: shopperEmailFilters.ownerIds.length ? shopperEmailFilters.ownerIds : undefined,
      }),
    enabled: canManageShopperEmails(currentUser),
    refetchOnReconnect: false,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    onSuccess: (response) => {
      setItems(response);
    },
  });

  const openCreateModal = React.useCallback(() => {
    setEntryModal({ open: true });
  }, []);

  const openEditModal = React.useCallback((item?: EntryModalState['item']) => {
    setEntryModal({ open: true, item });
  }, []);

  const closeEntryModal = React.useCallback(() => {
    setEntryModal({ open: false });
  }, []);

  const refetchShopperEmails = React.useCallback(async () => {
    await queryClient.invalidateQueries(['shopper-emails']);
    const response = await refetch();

    if (response.data) {
      setItems(response.data);
    }

    return response;
  }, [queryClient, refetch, setItems]);

  return {
    entryModal,
    items: items ?? data?.results ?? [],
    loading: isLoading,
    totalItemsCount: totalItemsCount || data?.count || 0,
    filters: shopperEmailFilters,
    setPage,
    setFilters,
    openCreateModal,
    openEditModal,
    closeEntryModal,
    refetchShopperEmails,
  };
};
